import { Trans, useTranslation } from "react-i18next"
import type { TFunction } from "react-i18next"
import type { CountryIntelligenceRulesProbation } from "@oysterhr/schema"
import { timeWithUnit } from "../../hooks/useKBAdvisor/helpers"
import { countryCodeToName } from "@oysterhr/common"

export const StatutoryMessage = ({
  statutory,
  countryCode,
  subdivisionName,
}: {
  statutory: CountryIntelligenceRulesProbation[]
  countryCode: string
  subdivisionName?: string
}) => {
  const { t } = useTranslation("knowledge_base/HiringAdvisor")

  const renderExtendable = ({ extension }: CountryIntelligenceRulesProbation) => {
    return (
      !!extension &&
      extension?.deadline &&
      extension?.maxAdditionalDuration &&
      t("probationPeriod.statutory.extendable", {
        extensionDeadline: timeWithUnit(t, extension?.deadline),
        extensionFactor: timeWithUnit(t, extension?.maxAdditionalDuration),
      })
    )
  }

  const getEmploymentLevelsText = (t: TFunction, rules: CountryIntelligenceRulesProbation) => {
    const { teamMemberLevel, teamMemberSeniorities } = rules
    if (teamMemberSeniorities?.length) {
      return t("probationPeriod.levelSpecific", {
        employmentLevel: teamMemberSeniorities
          .map(seniority =>
            t(`seniorityLevel.${seniority}`, {
              ns: "EngagementInfo",
            }),
          )
          .join(", "),
      })
    }
    if (teamMemberLevel) {
      return t("probationPeriod.levelSpecific", {
        employmentLevel: t(`enums.employmentLevel.${teamMemberLevel}`),
      })
    }
    return ""
  }

  const getTenureText = (t: TFunction, rules: CountryIntelligenceRulesProbation) => {
    const { tenureRange } = rules

    if (tenureRange?.min) {
      if (tenureRange.max) {
        return t("probationPeriod.tenureMinMax", {
          tenureMin: timeWithUnit(t, tenureRange.min),
          tenureMax: timeWithUnit(t, tenureRange.max),
        })
      }
      return t("probationPeriod.tenureMin", {
        tenureMin: timeWithUnit(t, tenureRange.min),
      })
    }
  }

  const renderStatutoryItem = (rules: CountryIntelligenceRulesProbation) => {
    const { duration, employmentType } = rules
    return (
      <Trans
        defaults={t("probationPeriod.statutory.item", {
          employmentType: t(`enums.employmentType.${employmentType}`),
          maxDuration: timeWithUnit(t, duration),
          levelSpecific: getEmploymentLevelsText(t, rules),
          tenure: getTenureText(t, rules),
        })}
        components={{
          b: <b />,
        }}
      ></Trans>
    )
  }

  return (
    <>
      <p>
        {t("probationPeriod.statutory.title", {
          countryName: [countryCodeToName(countryCode), subdivisionName].filter(Boolean).join(", "),
        })}
      </p>
      <ul className="list-inside list-disc py-2">
        {statutory?.map((probationItem, idx) => {
          return (
            <li key={idx} className="pl-4">
              {renderStatutoryItem(probationItem)} {renderExtendable(probationItem)}
            </li>
          )
        })}
      </ul>
    </>
  )
}
export const CustomaryMessage = ({
  customary,
}: {
  customary: CountryIntelligenceRulesProbation[]
  countryCode: string
}) => {
  const { t } = useTranslation("knowledge_base/HiringAdvisor")

  return (
    <>
      <p>{t("probationPeriod.customary.title")}</p>
      <ul className="list-inside list-disc py-2">
        {customary?.map((probationItem, idx) => {
          const { teamMemberLevel, duration, employmentType } = probationItem
          const { value, unit } = duration
          return (
            <li key={idx} className="pl-4">
              {t("probationPeriod.customary.item", {
                customaryDuration: timeWithUnit(t, { value, unit }),
                typeAndLevelSpecific: t("probationPeriod.typeAndLevelSpecific", {
                  employmentType: t(`enums.employmentType.${employmentType}`),
                  employmentLevel: teamMemberLevel
                    ? `${t(`enums.employmentLevel.${teamMemberLevel}`)} `
                    : "",
                }),
              })}
            </li>
          )
        })}
      </ul>
    </>
  )
}
export const RecommendedMessage = ({
  recommended,
}: {
  recommended: CountryIntelligenceRulesProbation[]
  countryCode: string
}) => {
  const { t } = useTranslation("knowledge_base/HiringAdvisor")

  return (
    <>
      <p>{t("probationPeriod.recommended.title")}</p>
      <ul className="list-inside list-disc py-2">
        {recommended?.map((probationItem, idx) => {
          const { teamMemberLevel, duration, employmentType } = probationItem
          const { value, unit } = duration
          return (
            <li key={idx} className="pl-4">
              {t("probationPeriod.recommended.item", {
                recommendedDuration: timeWithUnit(t, { value, unit }),
                typeAndLevelSpecific: t("probationPeriod.typeAndLevelSpecific", {
                  employmentType: t(`enums.employmentType.${employmentType}`),
                  employmentLevel: teamMemberLevel
                    ? `${t(`enums.employmentLevel.${teamMemberLevel}`)} `
                    : "",
                }),
              })}
            </li>
          )
        })}
      </ul>
    </>
  )
}
